/*
  UNIFIED LOOP WIDGET HANDLER
  This script combines the functionality of three separate Loop scripts:
  1. Strikethrough Price Handler
  2. One-Time Purchase Discount Badge Handler
  3. Subscribe Proper Discount Total Handler
  
  By unifying these into a single script, we eliminate potential conflicts
  and ensure coordinated updates to the Loop widget.
*/

(function () {
  // Avoid running on pages without Loop widgets
  if (!document.querySelector('.loop-widget-container')) return;

  // Store state to reduce unnecessary DOM updates
  const state = {
    lastKnownVariantIds: {},
    updating: false,
  };

  // MAIN UPDATE FUNCTION - COORDINATES ALL UPDATES
  function updateLoopWidget() {
    // Prevent concurrent updates
    if (state.updating) return;
    state.updating = true;

    try {
      // Get all product IDs from LOOP_WIDGET
      if (window.LOOP_WIDGET) {
        const productIds = Object.keys(window.LOOP_WIDGET);

        // Process each product
        productIds.forEach(productId => {
          // 1. Update strikethrough prices
          updateStrikethroughPrices(productId);

          // 2. Update one-time purchase discount badge
          updateOneTimeBadge(productId);

          // 3. Update subscription discount badge
          updateSubscriptionDiscount(productId);
        });
      }
    } finally {
      state.updating = false;
    }
  }

  // 1. STRIKETHROUGH PRICE HANDLER
  function updateStrikethroughPrices(productId) {
    const productData = window.LOOP_WIDGET?.[productId];
    if (!productData) return;

    const selectedVariantId =
      productData.selectedVariantId || productData.product?.variants?.[0]?.id;

    // Skip if variant hasn't changed
    if (selectedVariantId === state.lastKnownVariantIds[productId]) return;

    // Update known variant
    state.lastKnownVariantIds[productId] = selectedVariantId;

    const selectedVariant = productData.product?.variants?.find(
      variant => variant.id === selectedVariantId
    );

    if (!selectedVariant) return;

    const compareAtPrice = selectedVariant.compare_at_price;
    const currentPrice = selectedVariant.price;

    // Select both compare-at-price elements
    const compareAtPriceElements = document.querySelectorAll(
      `.loop-widget-purchase-option-compare-at-price`
    );

    const currentPriceElementOneTime = document.querySelector(
      `#loop-widget-purchase-option-price-onetime-${productId}`
    );

    // Format the compare-at price
    const formattedCompareAtPrice = compareAtPrice ? `$${(compareAtPrice / 100).toFixed(2)}` : null;

    // Handle One-time purchase compare-at price
    if (compareAtPrice && compareAtPrice > currentPrice && compareAtPriceElements[0]) {
      compareAtPriceElements[0].style.setProperty('display', 'block', 'important');
      compareAtPriceElements[0].innerHTML = formattedCompareAtPrice;
      compareAtPriceElements[0].style.textDecoration = 'line-through';
    } else if (compareAtPriceElements[0]) {
      compareAtPriceElements[0].style.display = 'none';
    }

    // Always show compare-at price for Subscribe and Save
    if (compareAtPriceElements[1] && formattedCompareAtPrice) {
      compareAtPriceElements[1].style.setProperty('display', 'block', 'important');
      compareAtPriceElements[1].innerHTML = formattedCompareAtPrice;
      compareAtPriceElements[1].style.textDecoration = 'line-through';
    } else if (compareAtPriceElements[1]) {
      compareAtPriceElements[1].style.display = 'none';
    }

    // Display current price for One-time purchase
    if (currentPriceElementOneTime) {
      currentPriceElementOneTime.innerHTML = `$${(currentPrice / 100).toFixed(2)}`;
    }
  }

  // 2. ONE-TIME PURCHASE DISCOUNT BADGE HANDLER
  function updateOneTimeBadge(productId) {
    // Find the badge element
    const badge = document.querySelector(
      '.loop-widget-purchase-option-onetime .loop-widget-purchase-option-discount-badge'
    );

    if (!badge) return;

    // Check if first variant is selected (we hide badge on first variant)
    const firstVariantInput = document.querySelector(
      '.variant-picker__option-values input[type="radio"]:first-child'
    );

    if (firstVariantInput && firstVariantInput.checked) {
      badge.classList.add('loop-display-none');
      return;
    }

    // Find price elements
    const compareAtPrice = document.querySelector(
      '.loop-widget-purchase-option-onetime .loop-widget-purchase-option-compare-at-price'
    );

    const currentPrice = document.querySelector(
      '.loop-widget-purchase-option-onetime .loop-widget-purchase-option-price'
    );

    if (!compareAtPrice || !currentPrice) {
      badge.classList.add('loop-display-none');
      return;
    }

    // Check if compare-at price is visible
    const compareAtVisible = window.getComputedStyle(compareAtPrice).display !== 'none';

    if (!compareAtVisible) {
      badge.classList.add('loop-display-none');
      return;
    }

    // Calculate savings
    const compareAtAmount = parseFloat(compareAtPrice.textContent.replace(/[^0-9.]/g, ''));
    const currentAmount = parseFloat(currentPrice.textContent.replace(/[^0-9.]/g, ''));

    if (isNaN(compareAtAmount) || isNaN(currentAmount)) {
      badge.classList.add('loop-display-none');
      return;
    }

    const savings = compareAtAmount - currentAmount;

    if (savings > 0) {
      badge.textContent = `Save $${savings.toFixed(2)}`;
      badge.classList.remove('loop-display-none');
    } else {
      badge.classList.add('loop-display-none');
    }
  }

  // 3. SUBSCRIPTION DISCOUNT TOTAL HANDLER
  function updateSubscriptionDiscount(productId) {
    const productData = window.LOOP_WIDGET?.[productId];
    if (!productData) return;

    const selectedVariantId = productData.selectedVariantId;
    const selectedSellingPlanId = productData.selectedSellingPlanId;

    // Skip if no selling plan selected (not subscription mode)
    if (!selectedSellingPlanId) return;

    const variants = productData.product?.variants;
    if (!variants) return;

    // Find the selected variant
    const selectedVariant = variants.find(variant => variant.id === selectedVariantId);
    if (!selectedVariant) return;

    // Get full compare-at price
    const fullCompareAtPrice = selectedVariant.compare_at_price;
    if (!fullCompareAtPrice) return;

    // Find the subscription price allocation
    const sellingPlanAllocation = selectedVariant.selling_plan_allocations?.find(
      allocation => allocation.selling_plan_id === selectedSellingPlanId
    );

    if (!sellingPlanAllocation) return;

    // Get subscription price
    const subscriptionPrice = sellingPlanAllocation.price;

    // Calculate discount
    const discountAmount = fullCompareAtPrice - subscriptionPrice;

    // Update the subscription discount badge
    const discountBadgeElement = document.querySelector(
      `[id*="loop-widget-purchase-option-discount-badge-id"]`
    );

    if (discountBadgeElement && discountAmount > 0) {
      discountBadgeElement.innerText = `Save $${(discountAmount / 100).toFixed(2)}`;
      discountBadgeElement.classList.remove('loop-display-none');
    }
  }

  // EVENT SETUP
  function setupEventListeners() {
    // 1. Listen for variant changes
    const variantContainer = document.querySelector('.variant-picker__option-values');
    if (variantContainer) {
      variantContainer.addEventListener('change', function (e) {
        if (e.target.type === 'radio') {
          // Use timeout to let DOM update first
          setTimeout(updateLoopWidget, 50);
        }
      });
    }

    // 2. Listen for Loop widget initialization and updates
    // Use DOM mutations to detect Loop widget changes
    const observer = new MutationObserver(function (mutations) {
      // Check if mutations affected the Loop widget
      const affectedLoop = mutations.some(mutation => {
        return mutation.target.closest('.loop-widget-container');
      });

      if (affectedLoop) {
        setTimeout(updateLoopWidget, 50);
      }
    });

    // Observe Loop widget container
    const loopContainer = document.querySelector('.loop-widget-container');
    if (loopContainer) {
      observer.observe(loopContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }

    // 3. Run initial update
    setTimeout(updateLoopWidget, 300);

    // 4. Check periodically a few times to catch any async updates
    let checkCount = 0;
    const checkInterval = setInterval(() => {
      updateLoopWidget();
      checkCount++;
      if (checkCount >= 3) clearInterval(checkInterval);
    }, 1000);
  }

  // INITIALIZATION
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupEventListeners);
  } else {
    setupEventListeners();
  }
})();
